import React, { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { fetchProduct, fetchProducts } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { Truck, RotateCcw, Award, ShoppingBag } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const { search } = useLocation();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams(search);
    const requestedColor = params.get("color");
    fetchProduct(slug)
      .then((p) => {
        setProduct(p);
        setSize(p.sizes?.[0] || null);
        // Use requested color from URL if valid, otherwise default
        setColor(
          requestedColor && p.colors?.includes(requestedColor) ? requestedColor : p.color
        );
        return fetchProducts(p.collection);
      })
      .then((all) => setRelated(all.filter((p) => p.slug !== slug).slice(0, 4)))
      .catch(() => {})
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug, search]);

  if (loading || !product) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="aspect-[4/5] bg-sand animate-pulse" />
          <div className="space-y-4">
            <div className="h-6 w-32 bg-sand animate-pulse" />
            <div className="h-12 w-3/4 bg-sand animate-pulse" />
            <div className="h-4 w-1/4 bg-sand animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  const onAdd = () => {
    if (product.sizes?.length > 1 && !size) {
      toast.error("Select a size");
      return;
    }
    addItem(product, { size, color, quantity: 1 });
    toast.success(`${product.name} added to bag`);
  };

  return (
    <div data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-8 pb-4 text-xs text-navy/50 uppercase tracking-widest">
        <Link to="/shop" className="hover:text-navy">Shop</Link>
        <span className="mx-2">/</span>
        <Link to={`/collections/${product.collection}`} className="hover:text-navy">
          {product.collection.replace(/-/g, " ")}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-navy">{product.name}</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
        {/* Gallery */}
        <div>
          <div className="bg-sand overflow-hidden aspect-square flex items-center justify-center mx-auto w-full max-w-[420px] md:max-w-none">
            <img
              src={(product.color_images && product.color_images[color]) || product.image}
              alt={`${product.name} - ${color}`}
              className="w-full h-full object-contain transition-opacity duration-300"
              data-testid="product-main-image"
            />
          </div>
        </div>

        {/* Info */}
        <div className="md:sticky md:top-32 md:self-start">
          {product.tag && (
            <span className="inline-block bg-coral text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 mb-4">
              {product.tag}
            </span>
          )}
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-navy leading-[1.05]" data-testid="product-title">
            {product.name}
          </h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-display text-2xl font-semibold text-navy" data-testid="product-price">
              ${product.price.toFixed(2)}
            </span>
          </div>

          <p className="mt-6 text-navy/70 leading-relaxed">{product.description}</p>

          {/* Color */}
          <div className="mt-8">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-navy/50 mb-3">
              Color: <span className="text-navy">{color}</span>
            </p>
            <div className="flex gap-2 flex-wrap">
              {product.colors.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  data-testid={`color-${c.toLowerCase()}`}
                  className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest border transition-colors ${
                    color === c
                      ? "border-navy bg-navy text-white"
                      : "border-navy/20 text-navy hover:border-navy"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Size */}
          {product.sizes?.length > 0 && (
            <div className="mt-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-navy/50 mb-3">
                Size {size && <span className="text-navy">: {size}</span>}
              </p>
              <div className="flex gap-2 flex-wrap">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`min-w-[44px] px-3 py-2.5 text-xs font-semibold uppercase tracking-widest border transition-colors ${
                      size === s
                        ? "border-navy bg-navy text-white"
                        : "border-navy/20 text-navy hover:border-navy"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={onAdd}
            data-testid="add-to-cart-button"
            className="mt-8 w-full bg-navy text-white py-4 text-xs font-bold uppercase tracking-[0.22em] hover:bg-seafoam hover:text-navy transition-colors flex items-center justify-center gap-2"
          >
            <ShoppingBag className="w-4 h-4" strokeWidth={2} />
            Add to Bag
          </button>

          <ul className="mt-8 grid grid-cols-3 gap-4 text-xs text-navy/60">
            <li className="flex flex-col items-center text-center gap-2">
              <Truck className="w-5 h-5 text-seafoam-600" strokeWidth={1.5} />
              <span>Free Shipping<br />over $75</span>
            </li>
            <li className="flex flex-col items-center text-center gap-2">
              <RotateCcw className="w-5 h-5 text-seafoam-600" strokeWidth={1.5} />
              <span>60-Day<br />Returns</span>
            </li>
            <li className="flex flex-col items-center text-center gap-2">
              <Award className="w-5 h-5 text-seafoam-600" strokeWidth={1.5} />
              <span>Premium<br />Materials</span>
            </li>
          </ul>
        </div>
      </div>

      {related.length > 0 && (
        <section className="py-16 md:py-24 px-6 md:px-12 bg-sand">
          <div className="max-w-7xl mx-auto">
            <p className="b2t-overline mb-3">Complete the Look</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy mb-10">You'll also love</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
